import React, { useState, useMemo } from 'react';
import {
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import '../Pages.css';
import PageHero from '../../components/Common/PageHero';
import ScreenerSearchControls from '../../components/Screener/ScreenerSearchControls';
import ScreenerTable from '../../components/Screener/ScreenerTable';
import MethodologySection from '../../components/Screener/MethodologySection';

interface ScreenerData {
  id: number;
  symbol: string;
  company: string;
  dataIntegrity: number;
  fundamentals: number;
  screening: number;
  deepAnalysis: number;
  aiEnhancement: number;
  portfolioOpt: number;
}

// Sample data - in real app this would come from an API
const MOCK_DATA: ScreenerData[] = [
  {
    id: 1,
    symbol: 'AAPL',
    company: 'Apple Inc.',
    dataIntegrity: 9.2,
    fundamentals: 8.5,
    screening: 8.8,
    deepAnalysis: 8.3,
    aiEnhancement: 8.7,
    portfolioOpt: 8.1
  },
  {
    id: 2,
    symbol: 'GOOGL',
    company: 'Alphabet Inc.',
    dataIntegrity: 9.0,
    fundamentals: 8.7,
    screening: 8.5,
    deepAnalysis: 8.9,
    aiEnhancement: 9.1,
    portfolioOpt: 8.4
  },
  {
    id: 3,
    symbol: 'MSFT',
    company: 'Microsoft Corporation',
    dataIntegrity: 9.3,
    fundamentals: 8.9,
    screening: 8.7,
    deepAnalysis: 8.8,
    aiEnhancement: 9.0,
    portfolioOpt: 8.6
  },
  {
    id: 4,
    symbol: 'AMZN',
    company: 'Amazon.com Inc.',
    dataIntegrity: 8.8,
    fundamentals: 7.9,
    screening: 8.2,
    deepAnalysis: 8.0,
    aiEnhancement: 8.4,
    portfolioOpt: 7.8
  },
  {
    id: 5,
    symbol: 'TSLA',
    company: 'Tesla Inc.',
    dataIntegrity: 8.5,
    fundamentals: 7.2,
    screening: 7.8,
    deepAnalysis: 7.5,
    aiEnhancement: 8.0,
    portfolioOpt: 7.3
  },
  {
    id: 6,
    symbol: 'NVDA',
    company: 'NVIDIA Corporation',
    dataIntegrity: 9.1,
    fundamentals: 9.2,
    screening: 9.0,
    deepAnalysis: 9.3,
    aiEnhancement: 9.5,
    portfolioOpt: 9.1
  },
  {
    id: 7,
    symbol: 'META',
    company: 'Meta Platforms Inc.',
    dataIntegrity: 8.7,
    fundamentals: 8.0,
    screening: 8.3,
    deepAnalysis: 8.1,
    aiEnhancement: 8.5,
    portfolioOpt: 7.9
  },
  {
    id: 8,
    symbol: 'JPM',
    company: 'JPMorgan Chase & Co.',
    dataIntegrity: 8.9,
    fundamentals: 8.6,
    screening: 8.4,
    deepAnalysis: 8.7,
    aiEnhancement: 8.3,
    portfolioOpt: 8.5
  }
];

const MarketSentiment: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Helper function for score styling
  const getScoreClass = (score: number) => {
    if (score >= 8) return 'score-high';
    if (score >= 6.5) return 'score-medium';
    return 'score-low';
  };

  // Methodology data aligned with Third Locust's 7-step process
  const methodologyItems = [
    {
      title: "Data Integrity",
      description: "Advanced multi-layer validation from 7 top financial data providers with precision assurance protocols"
    },
    {
      title: "Fundamentals",
      description: "Proprietary multifactor scoring across 28 financial ratios and proven valuation methodologies"
    },
    {
      title: "Quantitative Screening",
      description: "Systematic distillation of ~2,500 companies down to elite 8% exhibiting superior fundamentals"
    },
    {
      title: "Deep Analysis",
      description: "Forensic fundamental analysis examining competitive positioning, financial health, and value creation"
    },
    {
      title: "AI Enhancement",
      description: "Machine learning algorithms identify hidden market inefficiencies and high-probability opportunities"
    },
    {
      title: "Portfolio Optimization",
      description: "Strategic weightings optimized based on risk contribution, correlation, and alpha generation"
    }
  ];

  const scoreLegend = [
    { range: "8.0+", label: "Strong", className: "score-high" },
    { range: "6.5-7.9", label: "Moderate", className: "score-medium" },
    { range: "<6.5", label: "Weak", className: "score-low" }
  ];

  // Define columns with proper TypeScript types
  const columns: ColumnDef<ScreenerData>[] = useMemo(() => [
    {
      accessorKey: 'symbol',
      header: 'Symbol',
      cell: ({ getValue }) => <strong>{String(getValue())}</strong>,
      enableSorting: true,
    },
    {
      accessorKey: 'company',
      header: 'Company',
      cell: ({ getValue }) => String(getValue()),
      enableSorting: true,
    },
    {
      accessorKey: 'dataIntegrity',
      header: 'Data Integrity',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'fundamentals',
      header: 'Fundamentals', 
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'screening',
      header: 'Screening',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'deepAnalysis',
      header: 'Deep Analysis',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'aiEnhancement',
      header: 'AI Enhancement',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'portfolioOpt',
      header: 'Portfolio Opt.',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return (
          <span className={`score-value ${getScoreClass(value)}`}>
            {value.toFixed(1)}
          </span>
        );
      },
      enableSorting: true,
    },
  ], []);

  const table = useReactTable({
    data: MOCK_DATA,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    debugTable: true,
  });

  return (
    <div className="investment-screener-page">
      <PageHero
        title="Market Sentiment Tracker"
        subtitle="Third Locust's proprietary screening system analyzing Russell 2000 & S&P 500 stocks ($200M-$10B market cap) through our systematic 7-step investment process"
        className="investment-screener-hero"
      />

      <ScreenerSearchControls
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        resultsCount={table.getFilteredRowModel().rows.length}
      />

      <ScreenerTable table={table} />

      <MethodologySection
        methodologyItems={methodologyItems}
        scoreLegend={scoreLegend}
      />
    </div>
  );
};

export default MarketSentiment;
